import * as amplitude from "@amplitude/analytics-browser";
import moment from "moment";

import { app } from "js/namespaces";
import getLogger, { LogGroup } from "js/core/logger";
import { getBrowserInfo } from "js/core/utilities/browser";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.ANALYTICS);

function getFullDaysBetween(startTimestamp, endTimestamp) {
    const startDate = moment(parseInt(startTimestamp)).clone().startOf("day");
    const endDate = moment(parseInt(endTimestamp)).clone().startOf("day");
    return endDate.diff(startDate, "days");
}

// Initialize Amplitude analytics
export function initAmplitude(user, trackState) {
    if (!window.amplitudeApiKey) return;

    amplitude.init(window.amplitudeApiKey, user.uid, {
        autocapture: {
            elementInteractions: true,
        },
    });
    const identifyEvent = new amplitude.Identify();

    // User creation date properties are segmented this way due to Amplitude reporting restrictions
    const creationDate = moment(parseInt(user.metadata.createdAt));
    identifyEvent.set("user_created_utc_year", creationDate.year());
    identifyEvent.set("user_created_utc_month", creationDate.month() + 1); // Default indexes to 0, needs to be indexed to 1
    identifyEvent.set("user_created_utc_day", creationDate.date());

    // Onboarding survey results - may be missing or in wrong format for some users so we need validation checks
    const userInfo = app.user?.attributes?.userInfo || {};
    if (userInfo.companySize && (typeof userInfo.companySize === "string")) {
        identifyEvent.set("company_size", userInfo.companySize);
    }
    if (userInfo.presentationsType && Array.isArray(userInfo.presentationsType)) {
        identifyEvent.set("content_type", [...new Set(userInfo.presentationsType)].sort());
    }
    if (userInfo.userRole && (typeof userInfo.userRole === "string")) {
        identifyEvent.set("user_defined_role", userInfo.userRole);
    }
    if (userInfo.userType && (typeof userInfo.userType === "string")) {
        identifyEvent.set("user_type", userInfo.userType);
    }

    const workspaceIds = app.user?.workspacesMetadata?.map(workspace => workspace.id);
    identifyEvent.set("user_workspace_ids", workspaceIds || []);

    // We can add this event property now as it will be refreshed more than once per day
    trackState({ "user_age_days_at_event": getFullDaysBetween(user.metadata.createdAt, Date.now()) });

    amplitude.identify(identifyEvent);
}

// Track an event in Amplitude
export function trackAmplitude(eventId, event) {
    const remappedKeys = { "current_plan": "current_plan_nickname" };
    let amplitudeEvent = _.mapKeys(
        event.eventProperty,
        (value, key) => remappedKeys[key] || key
    );

    if (window.releaseVersion && window.releaseVersion !== "unknown") {
        amplitudeEvent.release_version = window.releaseVersion;
    }

    try {
        // Add browser information to each event
        const browserInfo = getBrowserInfo();
        amplitudeEvent.browser_family = browserInfo.family;
        amplitudeEvent.browser_name = browserInfo.name;
    } catch (error) {
        logger.error(error, "Error getting browser info");
    }

    amplitude.setGroup("Workspace ID", amplitudeEvent.workspace_id);
    amplitude.track(eventId, amplitudeEvent);
}
